// @flow

import _ from 'lodash';

import DateUtils from '../../utils/DateUtils';

import ApiObject from '../ApiObject';
import ApiConstants from '../../constants/ApiConstants';

import AvailabilityHour from './AvailabilityHour';
import AvailabilityHoursForDay from './AvailabilityHoursForDay';

export default class AvailabilityHours extends ApiObject {

  monday: AvailabilityHoursForDay = new AvailabilityHoursForDay();
  tuesday: AvailabilityHoursForDay = new AvailabilityHoursForDay();
  wednesday: AvailabilityHoursForDay = new AvailabilityHoursForDay();
  thursday: AvailabilityHoursForDay = new AvailabilityHoursForDay();
  friday: AvailabilityHoursForDay = new AvailabilityHoursForDay();
  saturday: AvailabilityHoursForDay = new AvailabilityHoursForDay();
  sunday: AvailabilityHoursForDay = new AvailabilityHoursForDay();

  // -- tools
  getForDay(day: DayIndex): AvailabilityHoursForDay {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        return this.monday;
      case ApiConstants.Day.TUESDAY:
        return this.tuesday;
      case ApiConstants.Day.WEDNESDAY:
        return this.wednesday;
      case ApiConstants.Day.THURSDAY:
        return this.thursday;
      case ApiConstants.Day.FRIDAY:
        return this.friday;
      case ApiConstants.Day.SATURDAY:
        return this.saturday;
      case ApiConstants.Day.SUNDAY:
        return this.sunday;
      default:
        return new AvailabilityHoursForDay();
    }
  }

  setForDay(day: DayIndex, availabilityHoursForDay: AvailabilityHoursForDay) {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        this.monday = availabilityHoursForDay;
        break;
      case ApiConstants.Day.TUESDAY:
        this.tuesday = availabilityHoursForDay;
        break;
      case ApiConstants.Day.WEDNESDAY:
        this.wednesday = availabilityHoursForDay;
        break;
      case ApiConstants.Day.THURSDAY:
        this.thursday = availabilityHoursForDay;
        break;
      case ApiConstants.Day.FRIDAY:
        this.friday = availabilityHoursForDay;
        break;
      case ApiConstants.Day.SATURDAY:
        this.saturday = availabilityHoursForDay;
        break;
      case ApiConstants.Day.SUNDAY:
        this.sunday = availabilityHoursForDay;
        break;
      default:
    }
  }

  addForDay(day: DayIndex, availabilityHour: AvailabilityHour) {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        this.monday.addHour(availabilityHour);
        break;
      case ApiConstants.Day.TUESDAY:
        this.tuesday.addHour(availabilityHour);
        break;
      case ApiConstants.Day.WEDNESDAY:
        this.wednesday.addHour(availabilityHour);
        break;
      case ApiConstants.Day.THURSDAY:
        this.thursday.addHour(availabilityHour);
        break;
      case ApiConstants.Day.FRIDAY:
        this.friday.addHour(availabilityHour);
        break;
      case ApiConstants.Day.SATURDAY:
        this.saturday.addHour(availabilityHour);
        break;
      case ApiConstants.Day.SUNDAY:
        this.sunday.addHour(availabilityHour);
        break;
      default:
    }
  }

  removeForDay(day: DayIndex, availabilityHour: AvailabilityHour) {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        this.monday.deleteHour(availabilityHour);
        break;
      case ApiConstants.Day.TUESDAY:
        this.tuesday.deleteHour(availabilityHour);
        break;
      case ApiConstants.Day.WEDNESDAY:
        this.wednesday.deleteHour(availabilityHour);
        break;
      case ApiConstants.Day.THURSDAY:
        this.thursday.deleteHour(availabilityHour);
        break;
      case ApiConstants.Day.FRIDAY:
        this.friday.deleteHour(availabilityHour);
        break;
      case ApiConstants.Day.SATURDAY:
        this.saturday.deleteHour(availabilityHour);
        break;
      case ApiConstants.Day.SUNDAY:
        this.sunday.deleteHour(availabilityHour);
        break;
      default:
    }
  }

  getSuggestAvailabilityHourForDay(day: number): AvailabilityHour {
    const allOpeningsHours: AvailabilityHoursForDay = this.getAllOpeningsHours();
    const availabilityHoursForDay = this.getForDay(day).hours;

    let result = {};
    _.each(allOpeningsHours, (availabilityHour: AvailabilityHour) => {
      const key = `${availabilityHour.opening}_${availabilityHour.closing}`;
      if (_.isUndefined(result[key])) {
        result[key] = {
          availabilityHour,
          count: 1
        }
      } else {
        result[key].count++;
      }
    });

    // lower count for already used opening hours for this day.
    if (!_.isEmpty(availabilityHoursForDay)) {
      _.each(availabilityHoursForDay, (availabilityHour: AvailabilityHour) => {
        const key = `${availabilityHour.opening}_${availabilityHour.closing}`;
        result[key].count = -1;
      });
    }

    // find most present
    if (!_.isEmpty(result)) {
      let maxCount = 0;
      let max = !_.isEmpty(availabilityHoursForDay) ? availabilityHoursForDay[0] : allOpeningsHours[0];

      _.forIn(result, (value) => {
        if (value.count > maxCount) {
          maxCount = value.count;
          max = value.availabilityHour;
        }
      });

      // return a copy, not a reference.
      return max.copy();
    }

    return this.getDefaultAvailabilityHour()
  }

  getSuggestAvailabilityHour(): AvailabilityHour {
    const allOpeningsHours: AvailabilityHoursForDay = this.getAllOpeningsHours();

    let result = {};
    _.each(allOpeningsHours, (availabilityHour: AvailabilityHour) => {
      const key = `${availabilityHour.opening}_${availabilityHour.closing}`;
      if (_.isUndefined(result[key])) {
        result[key] = {
          availabilityHour,
          count: 1
        }
      } else {
        result[key].count++;
      }
    });

    // find most present
    if (!_.isEmpty(result)) {
      let maxCount = 0;
      let max = new AvailabilityHour;

      _.forIn(result, (value) => {
        if (value.count > maxCount) {
          maxCount = value.count;
          max = value.availabilityHour;
        }
      });

      // return a copy, not a reference
      return max.copy();
    }

    return this.getDefaultAvailabilityHour()
  }

  getDefaultAvailabilityHour() {
    const newAvailabilityHour = new AvailabilityHour();
    newAvailabilityHour.setAvailableOnDay();
    return newAvailabilityHour;
  }

  getAllOpeningsHours(): AvailabilityHoursForDay {
    return _.concat(this.monday.hours, this.tuesday.hours,
      this.wednesday.hours, this.thursday.hours, this.friday.hours,
      this.saturday.hours, this.sunday.hours
    );
  }

  isAvailableForDate(date: Moment) {
    const dayIndex = DateUtils.getPmtDayIndex(date);

    const availabilityHoursForDay: AvailabilityHoursForDay = this.getForDay(dayIndex);

    return availabilityHoursForDay.isAvailableForDate(date);
  }

  /**
   * Warning: moment.js week start at sunday, not monday
   */
  haveAvailabilityForDay(dayIndex: DayIndex) {
    return this.getForDay(dayIndex).haveAvailibility();
  }

  isAvailableForWholeDay(dayIndex: DayIndex) {
    return this.getForDay(dayIndex).isAvailableForWholeDay();
  }

  setAvailableForWholeDayForDay(dayIndex: DayIndex) {
    const availabilityHoursForDay: AvailabilityHoursForDay = this.getForDay(dayIndex);
    availabilityHoursForDay.setAvailableForWholeDay();
    this.setForDay(dayIndex, availabilityHoursForDay);
  }

  // -- serialization

  postJsonAssignation(data: Object) {
    super.postJsonAssignation(data);

    this.monday = new AvailabilityHoursForDay().fromJson(data.monday);
    this.tuesday = new AvailabilityHoursForDay().fromJson(data.tuesday);
    this.wednesday = new AvailabilityHoursForDay().fromJson(data.wednesday);
    this.thursday = new AvailabilityHoursForDay().fromJson(data.thursday);
    this.friday = new AvailabilityHoursForDay().fromJson(data.friday);
    this.saturday = new AvailabilityHoursForDay().fromJson(data.saturday);
    this.sunday = new AvailabilityHoursForDay().fromJson(data.sunday);
  }
}
